/**
 * UC1 — request N serials from the DynamoDB + Valkey-buffer design, render each barcode, and
 * report the serial-space accounting alongside the latency.
 *
 * HONESTY RULES ENFORCED HERE, because they are behavioural requirements rather than styling:
 *   1. Every serial is badged LIVE or LOCAL RENDER. LOCAL means the browser built it with no
 *      backend: no high-water mark, no shared buffer, therefore NO uniqueness guarantee and NO
 *      latency measurement.
 *   2. Latency is shown only for real network calls and is labelled a browser-observed round trip,
 *      captioned as ONE SAMPLE — never dressed up as a p99.
 *   3. A failed request surfaces the real error. There is no silent fallback that could be
 *      mistaken for a successful live call.
 *   4. A placeholder API is called out explicitly, because it answers HTTP 200 and would otherwise
 *      look like a successful vend that returned nothing.
 *
 * The BUFFER BYPASS toggle drives the same request down the slow path — one DynamoDB UpdateItem per
 * serial, no Valkey — and the response carries `bufferBypassed: true` so the bypass is PROVABLE
 * from the wire rather than assumed. An instrument that cannot prove it engaged is not evidence.
 */

import { useState } from 'react';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Form from '@cloudscape-design/components/form';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Select from '@cloudscape-design/components/select';
import Button from '@cloudscape-design/components/button';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Toggle from '@cloudscape-design/components/toggle';
import Metrics from './Metrics.jsx';
import SerialCard from './SerialCard.jsx';
import RawJson from './RawJson.jsx';
import { Impb, barcodeFromApiItem } from '../lib/impb.js';
import { timedFetch, errorText, isPlaceholder } from '../lib/api.js';
import { RECLAMATION_OFF, UC1_CONSUME_ONCE, C05_C06_UNREACHABLE } from '../lib/caveats.js';

const CONSTRUCT_OPTIONS = [
  { value: 'C04', label: 'C04 — 22 digits, 9-digit MID, no ZIP' },
  { value: 'C03', label: 'C03 — 30 digits, 9-digit MID, ZIP5' },
  { value: 'C01', label: 'C01 — 34 digits, 9-digit MID, ZIP+4' },
  { value: 'C02', label: 'C02 — 34 digits, 11-digit serial' },
  { value: 'C07', label: 'C07 — 30 digits, 6-digit MID (channel AI 93)' },
];

// Fabricated MID/STC used ONLY by local render mode. The live path uses whatever the service's
// MID/STC env vars say — the browser never decides a real Mailer ID.
const LOCAL_MID_9 = '912345678';
const LOCAL_MID_6 = '912345';
const LOCAL_STC = '123';

export default function Uc1Panel({ mode, endpoints, live, liveProbe, metrics, onRefreshMetrics, refreshing }) {
  const [count, setCount] = useState('5');
  const [construct, setConstruct] = useState(CONSTRUCT_OPTIONS[0]);
  const [zip, setZip] = useState('20260');
  const [bypassBuffer, setBypassBuffer] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  // A page-local counter. Explicitly NOT a uniqueness guarantee — it resets on reload.
  const [localCursor, setLocalCursor] = useState(1);

  const spec = Impb.CONSTRUCTS[construct.value];

  function onConstructChange(option) {
    setConstruct(option);
    const s = Impb.CONSTRUCTS[option.value];
    if (s.hasRouting) setZip(s.zipLength === 9 ? '202600001' : '20260');
  }

  function buildLocal(constructId, zipValue, startAt) {
    const s = Impb.CONSTRUCTS[constructId];
    return Impb.buildBarcode({
      construct: constructId,
      serviceTypeCode: LOCAL_STC,
      mailerId: s.midLength === 6 ? LOCAL_MID_6 : LOCAL_MID_9,
      serial: startAt,
      zip: s.hasRouting ? zipValue : undefined,
    });
  }

  async function submit() {
    setError(null);
    setResult(null);
    setBusy(true);
    try {
      const n = Number(count);
      if (!Number.isInteger(n) || n < 1 || n > 1000) {
        throw new Error('count must be an integer 1..1000 per the API contract');
      }
      if (spec.hasRouting && !new RegExp(`^[0-9]{${spec.zipLength}}$`).test(zip)) {
        throw new Error(`construct ${construct.value} requires exactly ${spec.zipLength} ZIP digits`);
      }

      if (live) {
        const body = { count: n, construct: construct.value };
        if (spec.hasRouting) body.zip = zip;
        const res = await timedFetch(
          `${endpoints.serialApi}/v1/serials${bypassBuffer ? '?bypassBuffer=1' : ''}`,
          { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) },
          15000
        );
        if (!res.ok) throw new Error(errorText(res));

        const payload = res.json || {};
        // THE BYPASS MUST BE PROVABLE FROM THE RESPONSE, not assumed from the request. The service
        // emits `bufferBypassed: true` only when it really skipped Valkey, and the bypass can be
        // refused outright by configuration — so a request that asked for it and got a buffered
        // answer would otherwise be presented as a slow-path measurement it is not.
        if (bypassBuffer && !payload.bufferBypassed) {
          throw new Error(
            'the bypass was requested but the response did not report bufferBypassed: true, so this ' +
              'did NOT go down the single-item DynamoDB path — refusing to present it as one. The ' +
              'deployment can refuse the bypass by configuration.'
          );
        }
        const items = payload.serials || [];
        const cards = [];
        let mismatches = 0;
        items.forEach((item, i) => {
          try {
            const b = barcodeFromApiItem(item);
            if (b.hrMismatch) mismatches += 1;
            cards.push({ ok: true, barcode: b, index: i });
          } catch (err) {
            mismatches += 1;
            cards.push({ ok: false, error: err.message, index: i });
          }
        });
        setResult({
          kind: 'live',
          ms: res.ms,
          payload,
          cards,
          mismatches,
          placeholder: isPlaceholder(payload),
        });
        onRefreshMetrics();
      } else {
        const cards = [];
        for (let i = 0; i < n; i += 1) {
          cards.push({ ok: true, barcode: buildLocal(construct.value, zip, localCursor + i), index: i });
        }
        setLocalCursor(localCursor + n);
        setResult({ kind: 'local', cards });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <SpaceBetween size="l">
      <Metrics metrics={metrics} onRefresh={onRefreshMetrics} refreshing={refreshing} live={live} />

      {/*
        RECLAMATION STATUS, READ FROM THE RUNNING SERVICE.

        "The code shipped" and "the feature is on" are different claims. Reclamation and adaptive
        block size both DEFAULT OFF, so the only truthful source for their state is the running
        service's own health body — not the repo, and not this panel's assumptions.

        Three states, deliberately distinguished:
          - features absent   -> "unknown", because a service that does not report feature state
                                 cannot answer. Showing "off" there would be a guess as a fact.
          - reclamation false -> OFF, and the copy says the 120-day capacity ceiling still applies.
          - reclamation true  -> ON, with the assumption it rests on named, because if a Mailer ID
                                 can be minted quickly then reclamation is unnecessary complexity
                                 carrying real duplicate risk.
      */}
      <Container
        header={
          <Header variant="h2" description="Read from GET /v1/health — the running service's own answer, not this page's assumption.">
            Serial reclamation (120-day rotation)
          </Header>
        }
      >
        {!metrics || !metrics.features ? (
          <StatusIndicator type="pending">
            unknown — this service does not report feature state, so its reclamation setting cannot
            be stated as fact
          </StatusIndicator>
        ) : (
          <SpaceBetween size="s">
            <ColumnLayout columns={3} variant="text-grid">
              <div>
                <Box variant="awsui-key-label">Reclamation</Box>
                <StatusIndicator type={metrics.features.reclamation ? 'success' : 'stopped'}>
                  {metrics.features.reclamation ? 'ON' : 'OFF (default)'}
                </StatusIndicator>
              </div>
              <div>
                <Box variant="awsui-key-label">Adaptive block size</Box>
                <StatusIndicator type={metrics.features.adaptiveBlockSize ? 'success' : 'stopped'}>
                  {metrics.features.adaptiveBlockSize ? 'ON' : 'OFF (default)'}
                </StatusIndicator>
              </div>
              <div>
                <Box variant="awsui-key-label">Block size</Box>
                <Box>{typeof metrics.features.blockSize === 'number' ? metrics.features.blockSize.toLocaleString() : 'n/a'}</Box>
              </div>
            </ColumnLayout>
            {metrics.features.reclamation ? (
              <Alert type="info" header="Reclamation is ON — and it rests on an unconfirmed assumption">
                Aged-out serials return to rotation once their 120-day window closes, anchored on
                manifest date when known and issue + 128 days otherwise. This is only the right
                mechanism if provisioning a new Mailer ID is slow. If a MID can be minted in
                minutes, rotating the MID makes the serial space consume-once and reclamation
                becomes unnecessary complexity carrying real duplicate risk. That question is
                outstanding with the customer.
              </Alert>
            ) : (
              <Alert type="info" header="Reclamation is OFF, so the 120-day ceiling still applies">
                <SpaceBetween size="xs">
                  <Box variant="p" data-testid="uc1-reclamation-off">
                    {RECLAMATION_OFF}
                  </Box>
                  <Box variant="p" data-testid="uc1-consume-once">
                    {UC1_CONSUME_ONCE}
                  </Box>
                </SpaceBetween>
              </Alert>
            )}
          </SpaceBetween>
        )}
      </Container>

      <Container
        header={
          <Header
            variant="h2"
            description="POST /v1/serials — block (Hi-Lo) allocation against a DynamoDB high-water mark, vended through the shared external Valkey buffer."
          >
            Request serials
          </Header>
        }
      >
        <form onSubmit={(e) => { e.preventDefault(); submit(); }}>
          <Form
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="primary" loading={busy} data-testid="uc1-submit" onClick={submit}>
                  Vend serials
                </Button>
              </SpaceBetween>
            }
            errorText={error}
          >
            <ColumnLayout columns={3}>
              <FormField label="Count" description="1–1000 per the API contract">
                <Input
                  type="number"
                  value={count}
                  onChange={({ detail }) => setCount(detail.value)}
                  data-testid="uc1-count"
                />
              </FormField>
              <FormField
                label="Construct"
                description={`${spec.totalDigits} digits encoded, ${spec.serialLength}-digit serial (max ${'9'.repeat(spec.serialLength)}), channel AI ${spec.channelAI}`}
                constraintText={C05_C06_UNREACHABLE}
              >
                <Select
                  selectedOption={construct}
                  options={CONSTRUCT_OPTIONS}
                  onChange={({ detail }) => onConstructChange(detail.selectedOption)}
                  data-testid="uc1-construct"
                />
              </FormField>
              {spec.hasRouting ? (
                <FormField
                  label="Destination ZIP"
                  description={`exactly ${spec.zipLength} digits (${spec.zipLength === 9 ? 'ZIP+4' : 'ZIP5'}); encoded in the 420 routing AI`}
                >
                  <Input value={zip} onChange={({ detail }) => setZip(detail.value)} data-testid="uc1-zip" />
                </FormField>
              ) : (
                <FormField label="Destination ZIP" description="C04 carries no routing element — the contract requires zip to be absent">
                  <Input value="" disabled placeholder="not applicable to C04" />
                </FormField>
              )}
            </ColumnLayout>

            <Box margin={{ top: 'm' }}>
              <Toggle
                checked={bypassBuffer}
                onChange={({ detail }) => setBypassBuffer(detail.checked)}
                data-testid="uc1-bypass"
                description="Skips the Valkey buffer and goes straight to DynamoDB — one UpdateItem per serial. Slower on purpose: it is how the buffer's value is measured rather than asserted. The response must report bufferBypassed, or this panel treats the result as a failure rather than a slow-path measurement."
              >
                Bypass the buffer (single-item DynamoDB path)
              </Toggle>
            </Box>
          </Form>
        </form>
      </Container>

      {result ? (
        <SpaceBetween size="l">
          {result.kind === 'live' ? (
            <>
              {result.placeholder ? (
                <Alert
                  type="warning"
                  header="The endpoint answered, but it is the infra PLACEHOLDER — no serial was vended"
                  data-testid="uc1-placeholder-alert"
                >
                  <code>{endpoints.serialApi}</code> returned HTTP 200 with{' '}
                  <code>placeholder: true</code>, meaning <code>services/serial-api</code> is not
                  deployed behind this route yet. A 200 with an empty <code>serials</code> array is
                  <strong> not</strong> a successful vend, and this UI will not present it as one.
                  Use local render mode or the local stub to review barcode rendering.
                </Alert>
              ) : (
                <Alert type="success" header="Live response">
                  Vended by <code>{endpoints.serialApi}</code>, region partition{' '}
                  <code>{result.payload.regionId || 'unknown'}</code>
                  {result.payload.bufferBypassed ? ' — buffer BYPASSED (single-item DynamoDB path)' : ''}.
                </Alert>
              )}
              <Box data-testid="uc1-latency">
                <StatusIndicator type="info">
                  round trip <strong>{result.ms.toFixed(1)} ms</strong> for{' '}
                  {result.payload.serials ? result.payload.serials.length : 0} serial(s) —
                  browser-observed, includes TLS and network, <strong>excludes the Apigee hop</strong>.
                  This is <strong>one sample, not a p99</strong>; the distributions are on the{' '}
                  <strong>Load test</strong> tab.
                </StatusIndicator>
              </Box>
              {result.mismatches > 0 ? (
                <Alert type="error" header="Contract mismatch">
                  {result.mismatches} of {result.cards.length} response item(s) disagree with{' '}
                  <code>shared/impb</code>. Surfaced rather than hidden — this is an integration bug.
                </Alert>
              ) : null}
            </>
          ) : (
            <>
              <Alert type="warning" header="LOCAL RENDER MODE — no backend involved">
                {liveProbe && liveProbe.reason ? <>{liveProbe.reason} </> : null}
                Barcodes below are built in the browser by the same <code>impb.js</code> the services
                use, so the rendering is reviewable now. But there is{' '}
                <strong>no high-water mark, no shared buffer, and therefore no uniqueness guarantee</strong>,
                and <strong>no latency measurement</strong>. Serials are sequential from a page-local
                counter that resets on reload. Do not quote any figure from this mode.
              </Alert>
              <Box data-testid="uc1-latency">
                <StatusIndicator type="stopped">
                  no latency measured — local render, no network call
                </StatusIndicator>
              </Box>
            </>
          )}

          {result.cards.map((c) =>
            c.ok ? (
              <SerialCard
                key={`${c.index}-${c.barcode.pic}`}
                barcode={c.barcode}
                index={c.index}
                mode={mode}
                extra={{
                  live: result.kind === 'live',
                  regionId: result.payload && result.payload.regionId,
                  requestId: result.payload && result.payload.requestId,
                }}
              />
            ) : (
              <Alert key={c.index} type="error" header={`Serial #${c.index + 1} failed verification`}>
                {c.error}
              </Alert>
            )
          )}

          {result.payload ? <RawJson value={result.payload} /> : null}
        </SpaceBetween>
      ) : null}
    </SpaceBetween>
  );
}
